// Binary STL emitter for the rotor disc — a straight prism of the rotor's 2D
// profile (outer rim with the pole-0 datum notch, bore, magnet pockets), plus
// an optional back-face keyed recess (a stepped top) when the layout carries
// one. Pure: consumes RotorLayout only. The notch is an ANGULAR rectangle
// (radial cuts + chord bottom, <0.01mm apart at default dims) — 2026-07-07 STL
// spec, deliberate.
import earcut from 'earcut';
import { RotorLayout } from './pcb-stator-rotor';

export const STL_SEGMENTS = 128;          // outer + bore + recess circles
export const STL_POCKET_SEGMENTS = 32;    // round pockets

type Pt = [number, number];

/** Outer rim, CCW, with the notch spliced in at the notch angle. */
function outerLoop(layout: RotorLayout): Pt[] {
  const R = layout.discOuterRadiusMm;
  const thetaC = (layout.notch.angleDeg * Math.PI) / 180;
  const half = layout.notch.widthMm / 2 / R;
  const rIn = R - layout.notch.depthMm;
  const start = thetaC + half;
  const span = 2 * Math.PI - 2 * half;
  const pts: Pt[] = [];
  for (let i = 0; i <= STL_SEGMENTS; i++) {          // inclusive: both notch rim edges
    const a = start + (span * i) / STL_SEGMENTS;
    pts.push([R * Math.cos(a), R * Math.sin(a)]);
  }
  pts.push([rIn * Math.cos(thetaC - half), rIn * Math.sin(thetaC - half)]);
  pts.push([rIn * Math.cos(thetaC + half), rIn * Math.sin(thetaC + half)]);
  return pts;
}

function circleLoopCW(cx: number, cy: number, r: number, segments: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < segments; i++) {
    const a = -(2 * Math.PI * i) / segments;         // negative sweep = CW
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

function rectLoopCW(cx: number, cy: number, length: number, width: number, angleDeg: number): Pt[] {
  const a = (angleDeg * Math.PI) / 180;
  const ca = Math.cos(a), sa = Math.sin(a);
  const hl = length / 2, hw = width / 2;
  const localCCW: Pt[] = [[-hl, -hw], [hl, -hw], [hl, hw], [-hl, hw]];
  return localCCW.reverse()
    .map(([x, y]) => [cx + x * ca - y * sa, cy + x * sa + y * ca] as Pt);
}

/** CCW circle (positive sweep). Mirrors circleLoopCW for outer boundaries. */
function circleLoopCCW(cx: number, cy: number, r: number, segments: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (2 * Math.PI * i) / segments;
    pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

/** Recess outer boundary, CCW: a circle of radius `r` with an optional OUTWARD
 *  rectangular key finger spliced in at the datum — the exact mirror of the
 *  target ring's key TAB (pcb-stator-encoder-target buildOutline), so the slot
 *  mates the tab. Base corners sit on the circle (angle offset asin(halfW/r)),
 *  tip corners at `tipR`, tip pushed LEFT-then-RIGHT to avoid a bowtie. */
function recessOuterLoopCCW(
  r: number, segments: number,
  finger: { angleRad: number; halfWidthMm: number; tipR: number } | null,
): Pt[] {
  if (!finger) return circleLoopCCW(0, 0, r, segments);
  const angHalf = Math.asin(finger.halfWidthMm / r);
  const rightA = finger.angleRad + angHalf;
  const leftA = finger.angleRad - angHalf;
  const startA = rightA;
  const endA = leftA + 2 * Math.PI;               // long way round, tab base arc omitted
  const pts: Pt[] = [];
  for (let i = 0; i <= segments; i++) {           // inclusive: both base corners land on the circle
    const a = startA + (endA - startA) * (i / segments);
    pts.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  pts.push([finger.tipR * Math.cos(leftA), finger.tipR * Math.sin(leftA)]);
  pts.push([finger.tipR * Math.cos(rightA), finger.tipR * Math.sin(rightA)]);
  return pts;
}

/** earcut a region (outer loop first, then holes) and emit a flat cap at height
 *  `z`. faceUp keeps the input (CCW-outer) winding → +z normal; !faceUp reverses
 *  → −z. Same winding contract the walls rely on. */
function capTris(loops: Pt[][], z: number, faceUp: boolean): number[][][] {
  const flat: number[] = [];
  const holeIdx: number[] = [];
  for (let li = 0; li < loops.length; li++) {
    if (li > 0) holeIdx.push(flat.length / 2);
    for (const [x, y] of loops[li]) flat.push(x, y);
  }
  const idx = earcut(flat, holeIdx);
  const vx = (i: number): Pt => [flat[2 * i], flat[2 * i + 1]];
  const out: number[][][] = [];
  for (let i = 0; i < idx.length; i += 3) {
    const [a, b, c] = [vx(idx[i]), vx(idx[i + 1]), vx(idx[i + 2])];
    out.push(faceUp
      ? [[a[0], a[1], z], [b[0], b[1], z], [c[0], c[1], z]]
      : [[a[0], a[1], z], [c[0], c[1], z], [b[0], b[1], z]]);
  }
  return out;
}

/** Vertical wall for one loop between zLo and zHi. Same rule as the legacy
 *  walls: CCW loop faces outward, CW loop faces into its hole (both away from
 *  the solid). */
function wallTris(loop: Pt[], zLo: number, zHi: number): number[][][] {
  const out: number[][][] = [];
  for (let i = 0; i < loop.length; i++) {
    const [px, py] = loop[i];
    const [qx, qy] = loop[(i + 1) % loop.length];
    out.push([[px, py, zLo], [qx, qy, zLo], [qx, qy, zHi]]);
    out.push([[px, py, zLo], [qx, qy, zHi], [px, py, zHi]]);
  }
  return out;
}

export function generateRotorStl(layout: RotorLayout): ArrayBuffer {
  if (!layout.valid) return writeStl([]);
  return layout.recess ? generateWithRecess(layout) : generateLegacy(layout);
}

/** Straight prism of the 2D profile — the pre-encoder emitter, kept verbatim so
 *  a recess-free layout stays byte-identical to the golden. */
function generateLegacy(layout: RotorLayout): ArrayBuffer {
  const outer = outerLoop(layout);
  const holes: Pt[][] = [];
  if (layout.boreRadiusMm > 0) holes.push(circleLoopCW(0, 0, layout.boreRadiusMm, STL_SEGMENTS));
  for (const m of layout.magnets) {
    holes.push('dia' in m.pocketMm
      ? circleLoopCW(m.cx, m.cy, m.pocketMm.dia / 2, STL_POCKET_SEGMENTS)
      : rectLoopCW(m.cx, m.cy, m.pocketMm.length, m.pocketMm.width, m.angleDeg));
  }

  // earcut: flat coords + hole start indices
  const loops = [outer, ...holes];
  const flat: number[] = [];
  const holeIdx: number[] = [];
  for (let li = 0; li < loops.length; li++) {
    if (li > 0) holeIdx.push(flat.length / 2);
    for (const [x, y] of loops[li]) flat.push(x, y);
  }
  const capIdx = earcut(flat, holeIdx);              // triangle vertex indices, input winding
  const vx = (i: number): Pt => [flat[2 * i], flat[2 * i + 1]];
  const t = layout.thicknessMm;

  const tris: number[][][] = [];                     // [v0,v1,v2] each [x,y,z]
  for (let i = 0; i < capIdx.length; i += 3) {
    const [a, b, c] = [vx(capIdx[i]), vx(capIdx[i + 1]), vx(capIdx[i + 2])];
    tris.push([[a[0], a[1], t], [b[0], b[1], t], [c[0], c[1], t]]);       // top, CCW -> +z
    tris.push([[a[0], a[1], 0], [c[0], c[1], 0], [b[0], b[1], 0]]);       // bottom, reversed -> -z
  }
  // Side walls: same rule for every loop — CCW outer walls face out, CW hole
  // walls face into the hole (both = away from the solid).
  for (const loop of loops) {
    for (let i = 0; i < loop.length; i++) {
      const [px, py] = loop[i];
      const [qx, qy] = loop[(i + 1) % loop.length];
      tris.push([[px, py, 0], [qx, qy, 0], [qx, qy, t]]);
      tris.push([[px, py, 0], [qx, qy, t], [px, py, t]]);
    }
  }
  return writeStl(tris);
}

/** Prism with a back-face counterbore: the top surface steps down to the recess
 *  floor over the annulus [recess.rInMm, recess.rOutMm] (+ key finger). Same
 *  winding contract as the legacy path — CCW outer boundaries / CW holes for
 *  every cap, and the walls face away from the solid. The recess is guaranteed
 *  (by computeRotorLayout's pocket-clamp) not to touch the pockets, bore or rim,
 *  so every cap region is a simple non-overlapping polygon set. */
function generateWithRecess(layout: RotorLayout): ArrayBuffer {
  const rec = layout.recess!;
  const t = layout.thicknessMm;
  const zFloor = t - rec.depthMm;

  const outer = outerLoop(layout);                                    // disc rim + notch, CCW
  const bore = layout.boreRadiusMm > 0 ? circleLoopCW(0, 0, layout.boreRadiusMm, STL_SEGMENTS) : null;
  const pockets: Pt[][] = layout.magnets.map(m => 'dia' in m.pocketMm
    ? circleLoopCW(m.cx, m.cy, m.pocketMm.dia / 2, STL_POCKET_SEGMENTS)
    : rectLoopCW(m.cx, m.cy, m.pocketMm.length, m.pocketMm.width, m.angleDeg));

  const finger = rec.keySlot.depthMm > 0
    ? { angleRad: rec.keySlot.angleRad, halfWidthMm: rec.keySlot.widthMm / 2, tipR: rec.rOutMm + rec.keySlot.depthMm }
    : null;
  const recOutCCW = recessOuterLoopCCW(rec.rOutMm, STL_SEGMENTS, finger);
  const recInCCW = circleLoopCCW(0, 0, rec.rInMm, STL_SEGMENTS);
  const recOutCW = [...recOutCCW].reverse();
  const recInCW = [...recInCCW].reverse();

  const profile: Pt[][] = [outer, ...(bore ? [bore] : []), ...pockets];

  const tris: number[][][] = [];
  // Bottom cap (unchanged full profile) at z=0, facing −z.
  tris.push(...capTris(profile, 0, false));
  // Top, split into three +z-facing regions:
  //  inner ring [bore, recIn]
  tris.push(...capTris([recInCCW, ...(bore ? [bore] : [])], t, true));
  //  outer ring [recOut, disc] with the recess as a hole alongside the pockets
  tris.push(...capTris([outer, recOutCW, ...pockets], t, true));
  //  recess floor [recIn, recOut] (+finger) at z = t − depth
  tris.push(...capTris([recOutCCW, recInCW], zFloor, true));
  // Full-height walls: disc rim, bore, pockets (0 → t).
  tris.push(...wallTris(outer, 0, t));
  if (bore) tris.push(...wallTris(bore, 0, t));
  for (const p of pockets) tris.push(...wallTris(p, 0, t));
  // Recess walls (zFloor → t): inner (CCW → outward), outer (CW → inward),
  // both facing into the recess cavity.
  tris.push(...wallTris(recInCCW, zFloor, t));
  tris.push(...wallTris(recOutCW, zFloor, t));

  return writeStl(tris);
}

function writeStl(tris: number[][][]): ArrayBuffer {
  const buf = new ArrayBuffer(84 + 50 * tris.length);
  const dv = new DataView(buf);
  const header = 'PCB Stator rotor - binary STL';
  for (let i = 0; i < Math.min(80, header.length); i++) dv.setUint8(i, header.charCodeAt(i) & 0xff);
  dv.setUint32(80, tris.length, true);
  let o = 84;
  for (const [a, b, c] of tris) {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
    const wx = c[0] - a[0], wy = c[1] - a[1], wz = c[2] - a[2];
    let nx = uy * wz - uz * wy, ny = uz * wx - ux * wz, nz = ux * wy - uy * wx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    for (const val of [nx, ny, nz, ...a, ...b, ...c]) { dv.setFloat32(o, val, true); o += 4; }
    dv.setUint16(o, 0, true); o += 2;
  }
  return buf;
}
